import React, { useCallback, useMemo, useState } from 'react';

import {
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, TextField, Typography } from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';

import { ParityAnalysis, RevenueHooks } from './types';
import { parseCommaNumber } from './utils';

interface Props {
  analysis: ParityAnalysis;
  revenueHooks: RevenueHooks | null;
  ratePlanName: string;
  ratePlanCode: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

function generateParityEmail(
  analysis: ParityAnalysis,
  revenueHooks: RevenueHooks | null,
  ratePlanName: string,
  ratePlanCode: string,
): string {
  const severeDates = analysis.severityCounts?.severe ?? 0;
  const outOfParityPct = revenueHooks?.outOfParityPct ?? '[data unavailable]';
  const estimatedExtraRevenueMonthlyFormatted = revenueHooks?.estimatedExtraRevenueMonthly
    ? currencyFormatter(analysis.currency, parseCommaNumber(revenueHooks.estimatedExtraRevenueMonthly), 0)
    : '[data unavailable]';
  const cvrImprovementPct = revenueHooks?.cvrImprovementPct ?? '[data unavailable]';
  const totalImpressions = revenueHooks?.totalImpressions ?? '[data unavailable]';

  const daysWithoutRates = analysis.severityCounts?.no_comparison ?? 0;
  const ratePlanLabel = ratePlanCode ? `${ratePlanName} - ${ratePlanCode}` : ratePlanName;

  return `Hi [Partner Name],

We value our partnership and can see a strong opportunity to drive more bookings for ${analysis.propertyName} through Luxury Escapes.

At the moment, ${analysis.outOfParityCount} of ${analysis.totalDates} upcoming dates are out of rate parity, with ${severeDates} dates showing gaps greater than 10%. Because of this, around ${outOfParityPct}% of impressions are currently flagged as overpriced, which is limiting conversion.

Based on recent demand, we estimate this could be resulting in approximately ${estimatedExtraRevenueMonthlyFormatted} in missed revenue per month. Bringing rates back into alignment could increase bookings by around ${cvrImprovementPct}%.

The demand is definitely there, with ${totalImpressions} searches for your property in the past 30 days, so restoring parity should help capture that additional volume.

Could you please confirm:
- Is "${ratePlanLabel}" your best available public rate?
- Would you be able to review the loaded rates or push updated ARI so we can restore parity? We see that competitors have pricing on ${daysWithoutRates} days where we do not.

Once this is updated, we can monitor performance closely and work together to maximise bookings.

Thanks again for your partnership and please let me know once the rates have been reviewed.

Best,
[Your Name]`;
}

export default function EmailGenerator({
  analysis,
  revenueHooks,
  ratePlanName,
  ratePlanCode,
  expanded: controlledExpanded,
  onExpandedChange,
}: Props) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded ?? internalExpanded;
  const [copied, setCopied] = useState(false);

  const emailText = useMemo(
    () => generateParityEmail(analysis, revenueHooks, ratePlanName, ratePlanCode),
    [analysis, revenueHooks, ratePlanName, ratePlanCode],
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(emailText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available in all contexts
    }
  }, [emailText]);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => {
        if (onExpandedChange) {
          onExpandedChange(isExpanded);
        } else {
          setInternalExpanded(isExpanded);
        }
      }}
      sx={{ mt: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="action" fontSize="small" />
          <Typography variant="subtitle2">Email Summary</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
              size="small"
            >
              {copied ? 'Copied!' : 'Generate Partner Email'}
            </Button>
          </Box>
          <TextField
            multiline
            minRows={12}
            maxRows={24}
            value={emailText}
            InputProps={{ readOnly: true }}
            fullWidth
            size="small"
            sx={{
              '& .MuiInputBase-input': { fontFamily: 'inherit', fontSize: '0.875rem' },
            }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
